'use client';

import type { FC, HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

interface ListProps extends HTMLAttributes<HTMLDivElement> {
  /** Visual style. `inset` = iOS 26 Inset Grouped (rounded card on grouped bg). `plain` = no card chrome. */
  variant?: 'inset' | 'plain';
}

/**
 * iOS 26 Inset Grouped list container.
 *
 * Spec source: Figma file `iOS and iPadOS 26 (Community)` fileKey
 * PqsckPMtg0ISiS5Drlnkm7. The "Grouped Table View" container on the inset
 * example uses `rounded-[26px]` at canvas width 410px — scales to ~22px at
 * 375pt iPhone width. Bg = Backgrounds (Grouped)/Secondary (#1c1c1e dark).
 *
 * Children should be `Cell` components — they own the inter-row hairlines and
 * the `last:after:hidden` rule.
 */
export const List: FC<ListProps> = ({ children, variant = 'inset', className = '', ...props }) => {
  if (variant === 'plain') {
    return (
      <div className={cn('flex flex-col w-full', className)} {...props}>
        {children}
      </div>
    );
  }
  return (
    <div
      className={cn('rounded-[22px] bg-bg-secondary overflow-hidden w-full px-[8px] py-[10px]', className)}
      {...props}
    >
      {children}
    </div>
  );
};
